import { createSignal, onMount, Show, For } from "solid-js";
import Text from "../components/Text";
import { useApi, isOk } from "../api";
import type { ModJarInfoDto } from "../api/generated/schemas";
import styles from "./VektorUtils.module.css";

const FEATURES: [string, string, string][] = [
    ["fa-warehouse", "VEKTORUTILS_F_LAGER_TITLE", "VEKTORUTILS_F_LAGER_DESC"],
    ["fa-compress", "VEKTORUTILS_F_COMPRESS_TITLE", "VEKTORUTILS_F_COMPRESS_DESC"],
    ["fa-bullseye", "VEKTORUTILS_F_BONZE_TITLE", "VEKTORUTILS_F_BONZE_DESC"],
    ["fa-magnifying-glass", "VEKTORUTILS_F_TOOLS_TITLE", "VEKTORUTILS_F_TOOLS_DESC"],
];

const STEPS: [string, string][] = [
    ["VEKTORUTILS_STEP1_TITLE", "VEKTORUTILS_STEP1_DESC"],
    ["VEKTORUTILS_STEP2_TITLE", "VEKTORUTILS_STEP2_DESC"],
    ["VEKTORUTILS_STEP3_TITLE", "VEKTORUTILS_STEP3_DESC"],
    ["VEKTORUTILS_STEP4_TITLE", "VEKTORUTILS_STEP4_DESC"],
];

const DOWNLOAD_URL = "/api/vektorutils/download";

const FALLBACK: ModJarInfoDto = { available: false, version: null, mcVersion: "1.8.9", loader: "Forge" };

export default function VektorUtils() {
    const api = useApi();
    const [info, setInfo] = createSignal<ModJarInfoDto | null>(null);

    onMount(async () => {
        try {
            const res = await api.vektorUtils.info();
            setInfo(isOk(res.data) && res.data.value ? (res.data.value as ModJarInfoDto) : FALLBACK);
        } catch {
            setInfo(FALLBACK);
        }
    });

    const downloadBlock = () => (
        <Show
            when={info()?.available}
            fallback={
                <span class={styles.pending}>
                    <i class="fa-solid fa-circle-notch fa-spin" />
                    <Text val="VEKTORUTILS_PREPARING" />
                </span>
            }
        >
            <a class={styles.download} href={DOWNLOAD_URL} target="_blank" rel="external noopener">
                <span class={styles.downloadLabel}>
                    <i class="fa-solid fa-download" />
                    <Text val="VEKTORUTILS_DOWNLOAD" />
                </span>
                <Show when={info()?.version}>
                    <span class={styles.downloadVersion}>v{info()!.version} / Forge 1.8.9</span>
                </Show>
            </a>
        </Show>
    );

    return (
        <div class={styles.page}>
            <section class={styles.hero}>
                <div class={styles.heroInner}>
                    <p class={styles.eyebrow}><Text val="VEKTORUTILS_TAG" /></p>
                    <h1 class={styles.title}><Text val="VEKTORUTILS_HERO_TITLE" /></h1>
                    <p class={styles.sub}><Text val="VEKTORUTILS_HERO_SUB" /></p>
                    {downloadBlock()}
                </div>
                <div class={styles.stripe} />
            </section>

            <section class={styles.strip}>
                <For each={FEATURES}>{([icon, title, desc], i) => (
                    <div class={styles.stripItem}>
                        <span class={styles.stripNumber}>{String(i() + 1).padStart(2, "0")}</span>
                        <i class={`fa-solid ${icon} ${styles.stripIcon}`} />
                        <h2 class={styles.stripTitle}><Text val={title} /></h2>
                        <p class={styles.stripDesc}><Text val={desc} /></p>
                    </div>
                )}</For>
            </section>

            <section class={styles.setup}>
                <div class={styles.setupInner}>
                    <div class={styles.setupHead}>
                        <p class={styles.eyebrow}><Text val="VEKTORUTILS_SETUP_LABEL" /></p>
                        <h2 class={styles.setupTitle}><Text val="VEKTORUTILS_SETUP_TITLE" /></h2>
                    </div>
                    <ol class={styles.timeline}>
                        <For each={STEPS}>{([title, desc]) => (
                            <li class={styles.timelineItem}>
                                <h3 class={styles.timelineTitle}><Text val={title} /></h3>
                                <p class={styles.timelineDesc}><Text val={desc} /></p>
                            </li>
                        )}</For>
                    </ol>
                </div>
            </section>

            <section class={styles.cta}>
                <h2 class={styles.ctaTitle}><Text val="VEKTORUTILS_CTA_TITLE" /></h2>
                <p class={styles.ctaDesc}><Text val="VEKTORUTILS_CTA_DESC" /></p>
                {downloadBlock()}
            </section>
        </div>
    );
}
